import { motion } from "framer-motion";
import { Card } from "../../../components/ui/Card";
import { LockSimple, Lightning, Shield } from "@phosphor-icons/react";

const securityFeatures = [
  {
    icon: (
      <LockSimple
        size={32}
        weight="thin"
        className="group-hover:animate-subtle-float"
      />
    ),
    title: "Secure by Design",
    description:
      "SOC2 certified infrastructure with end-to-end encryption for your code",
    gradient: "from-white/5 to-white/0",
  },
  {
    icon: (
      <Lightning
        size={32}
        weight="thin"
        className="group-hover:animate-subtle-float"
      />
    ),
    title: "Privacy First",
    description:
      "Your code is never stored or used for training. Review, then forget",
    gradient: "from-white/5 to-white/0",
  },
  {
    icon: (
      <Shield
        size={32}
        weight="thin"
        className="group-hover:animate-subtle-float"
      />
    ),
    title: "Team Controls",
    description:
      "Simple but powerful access controls to manage your team's security",
    gradient: "from-white/5 to-white/0",
  },
];

export function Security() {
  return (
    <section id="security" className="relative py-32">
      {/* Aurora Effect */}
      <div className="absolute inset-0 overflow-hidden opacity-40">
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/30 rounded-full mix-blend-multiply filter blur-[128px] animate-aurora" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/30 rounded-full mix-blend-multiply filter blur-[128px] animate-aurora delay-[2s]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="aurora-gradient">Security You Can Trust</h2>
          <p className="text-xl text-white/70 max-w-2xl mx-auto">
            Simple, powerful protection for your code and team
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {securityFeatures.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card
                variant="hover"
                className="p-8 h-full flex flex-col items-center text-center group relative overflow-hidden"
              >
                {/* Gradient Border Effect */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background: `linear-gradient(to bottom, ${feature.gradient})`,
                    mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                    maskComposite: "exclude",
                    padding: "1px",
                  }}
                />
                <span className="relative mb-6">
                  <span className="relative z-10 transform text-white/70 group-hover:text-white transition-colors duration-300">
                    {feature.icon}
                  </span>
                </span>
                <h3 className="text-xl font-semibold mb-3 bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent">
                  {feature.title}
                </h3>
                <p className="text-white/70 leading-relaxed">
                  {feature.description}
                </p>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Trust Indicators */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 flex flex-wrap justify-center gap-8 text-white/60"
        >
          <span>Independent Security Audit</span>
          <span>•</span>
          <span>Open Source Friendly</span>
          <span>•</span>
          <span>99.9% Uptime</span>
        </motion.div>
      </div>
    </section>
  );
}
